import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, TextInput } from "@/components/register/Fields";
import { Logo } from "@/components/site/Header";

export default function AdminLogin() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", code: "" });
  const [mfa, setMfa] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!mfa) {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setMfa(data);
      } else {
        const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code: form.code });
        setUser(data);
        navigate("/admin");
      }
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-8">
        <Logo size={48} />
        <h1 className="mt-6 font-display font-bold text-3xl tracking-tight">Staff sign in</h1>
        <div className="mt-8 space-y-5">
          {!mfa && (
            <>
              <Field label="Email"><TextInput testid="login-email" type="email" autoComplete="username" value={form.email} onChange={set("email")} /></Field>
              <Field label="Password"><TextInput testid="login-password" type="password" autoComplete="current-password" value={form.password} onChange={set("password")} /></Field>
            </>
          )}
          {mfa?.mfa_setup_required && (
            <div data-testid="mfa-setup" className="rounded-2xl bg-white p-4 flex gap-4 items-center text-[#070a11]">
              <QRCodeSVG value={mfa.otpauth_uri} size={120} />
              <div className="text-xs">
                <p className="font-bold text-sm">Set up two-factor authentication</p>
                <p className="mt-1">Scan with Google Authenticator, 1Password or Authy, or enter this key:</p>
                <p data-testid="mfa-secret" className="mt-2 font-mono-epl break-all">{mfa.secret}</p>
              </div>
            </div>
          )}
          {mfa && (
            <Field label="6-digit authentication code">
              <TextInput testid="login-mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={form.code} onChange={set("code")} autoFocus />
            </Field>
          )}
          {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
          <button data-testid="login-submit" disabled={busy} className="btn-primary w-full disabled:opacity-60">
            {busy ? <Loader2 size={18} className="animate-spin" /> : mfa ? "Verify" : "Continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
